import {
    allVariants,
    collection,
    parentTypeFor,
    routePath,
} from "../_variants/index.js";
import { checkDOI, reviseDOI } from "./actions";
import { canRevise, doiRevisionQuality } from "./canRevise.js";
import { makeDocumentsAttachable } from "../Documents/attachable.js";
import { makeDrawingAttachable } from "../Drawings/attachable.js";

import ItemListView from "../../../components/ItemListView/ItemListView.vue"
import ItemView from "../../../components/ItemView/ItemView.vue"
import effectivitiesAttachable from "../Effectivities/attachable.js";
import { time } from "../../standardAttachables";
import { revisions as _revisions } from "../../standardAttachables";

const revisions = {
    ..._revisions,
    "headers": [
        ..._revisions.headers,
        {
            "headerName": "Compiler",
            "field": "Quality.Compiler.By"
        }, {
            "headerName": "Checker",
            "field": "Quality.Checker.By"
        }, {
            "headerName": "Approver",
            "field": "Quality.Approver.By"
        }
    ]
};

const dataFields = [{
    "label": "Number",
    "key": "Number"
}, {
    "label": "Parent",
    "key": "Parent",
    "link": "Database/:ParentType/:ParentID"
}, {
    "label": "Current Revision Approved",
    "type": "bool",
    "compute": ({item}) => canRevise(item)
}, {
    "label": "Comment",
    "key": "Comment",
    "class": "md:col-span-3"
}, {
    "label": "External Items",
    "key": "Externals",
    "class": "md:col-span-3"
}];

/**
 * Build the list + detail vue-router routes for the DOI register matching
 * the supplied variant.
 *
 * @param {import("../_variants/index.js").Variant} variant
 */
export const makeDOIRoutes = (variant) => {
    const isMilitary = variant.key === "military";
    const labelSingular = isMilitary ? "Military DOI" : "DOI";
    const labelPlural = isMilitary ? "Military DOIs" : "DOIs";
    const doiParentType = parentTypeFor(variant, "DOIs");
    const doiCollection = collection(variant, "DOIs");
    const listPath = routePath(variant, "DOIs");

    return [{
        "path": listPath,
        "component": {...ItemListView},
        "name": labelPlural,
        "meta": {
            "app": variant.app,
            "name": labelPlural,
            "sideNav": true,
            "sideNavSection": "Design Items",
            "appSwitcher": false,
            "icon": variant.appIcon,
            "tableSettings": doiParentType,
            "dataPath": doiCollection,
            "selectionRoute": `Database/${doiParentType}/{ID}`
        }
    }, {
        "path": `${listPath}/:id`,
        "component": ItemView,
        "name": labelSingular,
        "meta": {
            "app": variant.app,
            "name": labelPlural,
            "dataPath": doiCollection,
            "selectionRoute": `Database/${doiParentType}/{ID}`,
            title(item) {
                return `${labelPlural}: ${item.Number}`
            },
            "attachedItems": {
                "Revisions": {
                    "attachables": [revisions]
                },
                "Attached Items": {
                    "attachables": [makeDocumentsAttachable(variant), makeDrawingAttachable(variant), effectivitiesAttachable]
                },
                "Additional Information": {
                    "attachables": [time]
                }
            },
            "preventAttachedItemsActions": true,
            "preventAttachedItemsRowActions": false,
            "dataFields": dataFields,
            "menuActions": [{
                "label": `Revise ${labelSingular}`,
                "action": reviseDOI,
                show({ pageItem }){ 
                    return [canRevise(pageItem), "The DOI is not approved"]
                }
            }, {
                "label": "Compiler Check",
                "action": checkDOI("Compiler"),
                "break": true,
                show({ pageItem }) {
                    return doiRevisionQuality(pageItem)?.Compiler?.Completed === true ? [false, "Already Complier Checked"] : [true, ""];
                }
            },
            {
                "label": "Checker Check",
                "action": checkDOI("Checker"),
                show({ pageItem }) {
                    return doiRevisionQuality(pageItem)?.Checker?.Completed === true ?
                        [false, "Already Complier Checked"] :
                        !doiRevisionQuality(pageItem)?.Compiler?.Completed ?
                            [false, "Document not checked by the complier"] :
                            [true, ""]
                }
            },
            {
                "label": "Approver Check",
                "action": checkDOI("Approver"),
                show({ pageItem }) {
                    return doiRevisionQuality(pageItem)?.Approver?.Completed === true ?
                        [false, "Already Approver Checked"] :
                        !doiRevisionQuality(pageItem)?.Checker?.Completed ?
                            [false, "Document not checked by the checker"] :
                            [true, ""];
                }
            }],
            "formMetadata": {
                "ParentType": doiParentType
            }
        }
    }];
};

export default allVariants().flatMap(makeDOIRoutes);
